// /admin — every tenant, plus the global spend readout PRD 17 §3 named as the
// operator's blind spot ("the /try daily spend cap can be hit with no visible
// signal").
//
// No filter, no pager, no server-side sort, no charts: one operator and a
// handful of tenants. The Table's own 25-row cap and count line are enough.

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
  Table,
} from "../../ui";
import type { BadgeTone, TableColumn } from "../../ui";
import { Link } from "@tanstack/react-router";
import { adminHrefs } from "../../shell/hrefs";
import { relativeTime } from "../content/relativeTime";
import { useSpend, useTenants } from "./api";
import type { LicenseState, TenantRow } from "./api";
import "./admin.css";

const STATE_TONE: Record<LicenseState, BadgeTone> = {
  active: "nominal",
  trial: "accent",
  expired: "flagged",
  unknown: "neutral",
};

function columns(): TableColumn<TenantRow>[] {
  return [
    {
      key: "org",
      header: "Org",
      cell: (row) => (
        <Link to={adminHrefs().tenant(row.org_slug)} className="type-label">
          {row.org_slug}
        </Link>
      ),
    },
    { key: "plan", header: "Plan", cell: (row) => row.plan ?? "—" },
    {
      key: "state",
      header: "State",
      cell: (row) => (
        <Badge tone={STATE_TONE[row.resolved_state]} lamp>
          {row.resolved_state}
          {row.days_remaining !== null ? ` · ${row.days_remaining}d` : ""}
        </Badge>
      ),
    },
    { key: "docs", header: "Documents", align: "right", cell: (row) => row.doc_count },
    { key: "keys", header: "Active keys", align: "right", cell: (row) => row.active_key_count },
    {
      key: "sessions",
      header: "Visits, 30d",
      align: "right",
      cell: (row) => row.sessions_recent,
    },
    {
      key: "since",
      header: "Licensed",
      cell: (row) => relativeTime(row.created_at),
    },
  ];
}

export function AdminConsoleScreen() {
  const tenants = useTenants();
  const spend = useSpend();

  const header = (
    <PageHeader
      eyebrow="Operator"
      title="Tenants"
      description="Every org this deployment knows about. Prospects that self-started a trial by mounting the widget are excluded."
    />
  );

  if (tenants.isPending) {
    return (
      <div className="maisight-admin">
        {header}
        <SkeletonStack label="Loading tenants">
          <Skeleton width={280} height={28} />
          <Skeleton width={620} />
          <Skeleton width={452} />
        </SkeletonStack>
      </div>
    );
  }

  if (tenants.isError || !tenants.data) {
    return (
      <div className="maisight-admin">
        {header}
        <ErrorState
          headline="Could not load the tenant list"
          detail={tenants.error?.message}
          action={
            <Button variant="primary" onClick={() => void tenants.refetch()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="maisight-admin">
      {header}
      <StatRow>
        <StatTile label="Tenants" value={tenants.data.length} />
        <StatTile
          label="Preview spend today"
          // A failed read must never render as $0.00 — that reads as "nothing
          // was spent", which is the opposite of "we do not know".
          value={spend.data ? `$${spend.data.try_spend_today_usd.toFixed(2)}` : "—"}
          detail={
            spend.data
              ? `of $${spend.data.try_daily_cap_usd.toFixed(2)} cap`
              : "spend unavailable"
          }
        />
        <StatTile
          label="Documents"
          value={tenants.data.reduce((sum, row) => sum + row.doc_count, 0)}
        />
      </StatRow>

      {tenants.data.length === 0 ? (
        <EmptyState
          icon="bar-chart"
          headline="No tenants yet"
          body="Issue a license key and the org appears here."
        />
      ) : (
        <Card tone="flat" header="All tenants">
          <Table<TenantRow>
            label="Tenants"
            columns={columns()}
            rows={tenants.data}
            rowKey={(row) => row.org_slug}
            total={tenants.data.length}
            unit="tenants"
          />
        </Card>
      )}
    </div>
  );
}
